/**
 * The change card: one conversation per part of the room, kept on the server, so
 * opening a part again shows everything said about it and where it was left.
 *
 * The host writes what should change, with pictures, videos or PDFs attached by
 * +, paste or drop. Flash answers in the thread and the part changes. The latest
 * change can be held to see the part as it was (Before) or undone; any earlier
 * change can be taken back with everything after it, in this part only. A part
 * whose change is taken back keeps its words in the thread, greyed.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import type { LibraryAsset, PageAttachment, PageMessage } from "../../../shared/types";
import { Icon, type IconName } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import type { Fit } from "../../assets/library";
import { openAssets } from "../../assets/store";
import { useNoteStream } from "../../lib/noteStream";
import { mmss } from "../make/time";
import { useNoteMedia } from "./noteMedia";

/** At most this many files ride on one note, as the page designer takes them. */
export const MAX_FILES = 4;

/** What a note can carry: pictures, PDFs and videos kept in Assets (a room's replay is not one). */
const NOTE_FIT: Fit = {
  label: "pictures, videos and PDFs",
  hint: "",
  accept: "image/*,video/*,.pdf",
  at: "all",
  test: (a) => (a.kind === "image" || a.kind === "pdf" || a.kind === "video") && a.source !== "room",
  why: (a) => (a.source === "room" ? "It’s a room’s replay." : "A note carries pictures, videos and PDFs."),
};

/** A file picked in Assets, as a note carries it: a video by its note, anything else by its key. */
export function attachmentOf(a: LibraryAsset): PageAttachment {
  if (a.kind === "video" && a.note_id) {
    return {
      key: a.note_id,
      url: "",
      name: a.name,
      mime: a.mime,
      kind: "video",
      video: { id: a.note_id, ...(a.duration_sec ? { duration: mmss(a.duration_sec) } : {}) },
    };
  }
  return { key: a.key ?? "", url: a.url ?? "", name: a.name, mime: a.mime, kind: a.kind === "image" ? "image" : "file" };
}

/** A video said into the chat plays where it was said, once Assets has it ready. */
function ChatVideo({ f }: { f: PageAttachment }) {
  const { src, poster } = useNoteMedia(f.video?.id);
  const ref = useRef<HTMLVideoElement>(null);
  const stream = useNoteStream(ref, src);
  if (src) {
    return (
      <video
        ref={ref}
        className="bw-mvid"
        src={stream ? undefined : src}
        poster={poster ?? undefined}
        controls
        playsInline
        preload="metadata"
        aria-label={f.name}
      />
    );
  }
  return (
    <span className={cx("bw-mpic", !poster && "bw-mpicv")}>
      {poster ? (
        <img src={poster} alt={f.name} />
      ) : (
        <span className="bw-mvname">
          {f.name}
          {f.video?.duration ? ` · ${f.video.duration}` : ""}
        </span>
      )}
      <i>
        <Icon name="playf" fill />
      </i>
    </span>
  );
}

/** What one attached file looks like in a message: a picture, a video, or a document. */
export function FileFace({ f }: { f: PageAttachment }) {
  if (f.kind === "image" && f.url) {
    return (
      <span className="bw-mpic">
        <img src={f.url} alt={f.name} />
      </span>
    );
  }
  if (f.kind === "video") return <ChatVideo f={f} />;
  return (
    <span className="bw-mdoc">
      <Icon name="file" />
      <span>{f.name}</span>
    </span>
  );
}

/** The same file as a chip in the box, before it is sent. */
export function FileChip({ f, onOff }: { f: PageAttachment; onOff: () => void }) {
  // A video shows its frame, as a picture shows itself.
  const poster = useNoteMedia(f.kind === "video" ? f.video?.id : undefined).poster;
  const pic = f.kind === "image" && f.url ? f.url : poster;
  return (
    <span className="bw-att">
      <span className={cx("bw-att-t", !pic && "doc")}>{pic ? <img src={pic} alt="" /> : <Icon name={f.kind === "video" ? "video" : "file"} />}</span>
      <span className="bw-att-n">
        {f.name}
        {f.video?.duration ? <em>{f.video.duration}</em> : null}
      </span>
      <button type="button" className="bw-att-x" aria-label={`Take ${f.name} off`} onClick={onOff}>
        <Icon name="x" />
      </button>
    </span>
  );
}

interface WalkChatProps {
  /** What the card is about: "The headline", "The whole page", "The poll at 12:40". */
  title: string;
  thread: readonly PageMessage[];
  /** Flash is working on this part. */
  busy: boolean;
  onAsk: (text: string, files: PageAttachment[]) => Promise<boolean>;
  onBack: (messageId: string) => Promise<void> | void;
  /** Hold to see the part as it was before the latest change. */
  onPeek?: (on: boolean) => void;
  onClose: () => void;
  /** What the box says before anything is typed. */
  placeholder: string;
  /** Something done by hand, not by Flash, as a button under the conversation: "Split or join chapters". */
  acts?: ReadonlyArray<{ label: string; icon: IconName; onClick: () => void }>;
  error?: string;
  /** The box takes the caret as the card opens; not when the click that opened it was to type into the words. */
  focus?: boolean;
}

/**
 * The words and files of one note to Flash: what is typed, what + adds from the one From Assets picker, and sending it
 * with nothing lost when the change did not go through. The computer's card and the phone's chat both use it.
 */
export function useNoteComposer(onAsk: (text: string, files: PageAttachment[]) => Promise<boolean>, busy: boolean) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState<PageAttachment[]>([]);
  const [said, setSaid] = useState("");

  /** Assets opens to add a file; files pasted or dropped go up in it and are added when they are in. */
  const attach = (incoming?: File[]) => {
    if (files.length >= MAX_FILES) {
      setSaid("Four files at most in one note.");
      return;
    }
    openAssets({
      fits: NOTE_FIT,
      title: "Add to the note",
      cta: "Add it",
      incoming,
      onPick: (a) =>
        setFiles((was) => {
          if (was.length >= MAX_FILES) {
            setSaid("Four files at most in one note.");
            return was;
          }
          return [...was, attachmentOf(a)];
        }),
    });
  };

  const send = async () => {
    const t = text.trim();
    if ((!t && files.length === 0) || busy) return;
    const sent = files;
    setText("");
    setFiles([]);
    setSaid("");
    const ok = await onAsk(t, sent);
    if (!ok) {
      // Nothing typed is lost when the change did not go through.
      setText(t);
      setFiles(sent);
    }
  };
  const paste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pics = [...(e.clipboardData?.files ?? [])];
    if (pics.length) {
      e.preventDefault();
      attach(pics);
    }
  };
  return { text, setText, files, setFiles, said, attach, send, paste };
}

export function WalkChat({ title, thread, busy, onAsk, onBack, onPeek, onClose, placeholder, acts, error, focus = true }: WalkChatProps) {
  const { text, setText, files, setFiles, said, attach, send, paste } = useNoteComposer(onAsk, busy);
  const [over, setOver] = useState(false);
  const [holding, setHolding] = useState(false);
  const log = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  // The newest message is always in view, on opening and after every reply.
  useLayoutEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread.length, busy]);
  // Why not on a phone: the card is a sheet there, and a keyboard opening over it would hide what it is about.
  useEffect(() => {
    if (focus) box.current?.focus({ preventScroll: true });
    // Only as the card opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const key = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // A window open over the card (Assets, a sheet) takes Esc for itself.
      if (document.querySelector(".scrim:not(.bw-cardscrim)")) return;
      onClose();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const keys = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const dropped = [...(e.dataTransfer?.files ?? [])];
    if (dropped.length) attach(dropped);
  };

  /** Before is held down, and let go: the part shows as it was only while it is held. */
  const hold = (on: boolean) => {
    if (on === holding) return;
    setHolding(on);
    onPeek?.(on);
  };
  // A card that closes while Before is held lets it go.
  const peekNow = useRef(onPeek);
  peekNow.current = onPeek;
  const heldNow = useRef(holding);
  heldNow.current = holding;
  useEffect(() => () => void (heldNow.current && peekNow.current?.(false)), []);

  // A change taken back leaves the card, as it always has; the server keeps it in the thread, greyed.
  const shown = thread.filter((m) => !m.taken);
  const lastChange = shown.findLast((m) => m.who === "ai" && m.before !== undefined);

  return (
    <div
      className={cx("bw-card bw-chat in", busy && "busy", over && "drop")}
      role="dialog"
      aria-label={title}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <div className="bw-ch">
        <span className="bw-cl">{title}</span>
        <button type="button" className="ibtn sm" aria-label="Close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <div className={cx("bw-cb", !shown.length && !busy && "empty")} ref={log} aria-live="polite">
        {shown.map((m) =>
          m.who === "me" ? (
            <div key={m.id} className="bw-m me">
              <div className="bw-mb">
                {m.text ? <p>{m.text}</p> : null}
                {m.files?.length ? (
                  <div className="bw-mfiles">
                    {m.files.map((f) => (
                      <FileFace f={f} key={f.key} />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div key={m.id} className="bw-m fl">
              <span className="bw-av" aria-hidden="true">
                <Icon name="sparkle" />
              </span>
              <div className="bw-mb">
                <p>{m.text}</p>
                {m.before !== undefined ? (
                  <div className="bw-macts">
                    {lastChange?.id === m.id ? (
                      <>
                        {onPeek ? (
                          <button
                            type="button"
                            className={cx("bw-hold", holding && "on")}
                            title="Hold to see how it was"
                            onPointerDown={() => hold(true)}
                            onPointerUp={() => hold(false)}
                            onPointerLeave={() => hold(false)}
                            onPointerCancel={() => hold(false)}
                            onKeyDown={(e) => (e.key === " " || e.key === "Enter") && !e.repeat && hold(true)}
                            onKeyUp={() => hold(false)}
                            onBlur={() => hold(false)}
                          >
                            <Icon name="eye" /> Before
                          </button>
                        ) : null}
                        <button type="button" onClick={() => void onBack(m.id)}>
                          <Icon name="undo" /> Undo
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => void onBack(m.id)}>
                        <Icon name="undo" /> Back to before this
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          ),
        )}
        {busy ? (
          <div className="bw-m fl">
            <span className="bw-av" aria-hidden="true">
              <Icon name="sparkle" />
            </span>
            <div className="bw-mb bw-dots" aria-label="Flash is working">
              <i />
              <i />
              <i />
            </div>
          </div>
        ) : null}
      </div>
      {acts?.length && !busy ? (
        <div className="bw-cacts">
          {acts.map((a) => (
            <button type="button" key={a.label} className="btn s" onClick={a.onClick}>
              <Icon name={a.icon} /> {a.label}
            </button>
          ))}
        </div>
      ) : null}
      {error || said ? (
        <p className="bw-cerr" role="alert">
          {error || said}
        </p>
      ) : null}
      <div className="bw-comp">
        {files.length ? (
          <div className="bw-atts">
            {files.map((f, i) => (
              <FileChip key={f.key} f={f} onOff={() => setFiles((was) => was.filter((_, k) => k !== i))} />
            ))}
          </div>
        ) : null}
        <div className="bw-cask">
          <button type="button" className="ibtn bw-attb" aria-label="Attach a picture, a video or a PDF" title="Attach a picture, a video or a PDF" onClick={() => attach()}>
            <Icon name="plus" />
          </button>
          <textarea dir="auto"
            ref={box}
            className="inp"
            rows={1}
            value={text}
            placeholder={placeholder}
            aria-label={placeholder}
            onChange={(e) => {
              setText(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(132, e.target.scrollHeight + 2)}px`;
            }}
            onKeyDown={keys}
            onPaste={paste}
          />
          <button type="button" className="ibtn bw-send" aria-label="Send" disabled={busy || (!text.trim() && !files.length)} onClick={() => void send()}>
            <Icon name="send" />
          </button>
        </div>
      </div>
    </div>
  );
}
